import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import NavMenu from '../components/ui/nav-menu';
import FooterNav from '../components/ui/footer-nav';
import ModuleRail from '../components/ui/module-rail';
import HeaderTasksButton from '../components/ui/header-tasks-button';
import { ArrowLeft, ShieldCheck, Copy, Check, Loader2, MapPin, Calendar, User, Building2, PenTool, Trash2, Palette, CheckCircle2, FileText, Send } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signature Pad Component
const SignaturePad = ({ onSignatureChange, signatureData }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set up canvas
    ctx.strokeStyle = '#1F2A37';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // If there's existing signature data, draw it
    if (signatureData) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = signatureData;
    }
  }, [signatureData]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.closePath();
    setIsDrawing(false);
    
    // Save signature data
    const data = canvas.toDataURL('image/png');
    onSignatureChange(data, hasSignature);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange(null, false);
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        data-testid="signature-canvas"
        className="w-full h-[120px] border-2 border-slate-300 rounded-lg bg-white cursor-crosshair touch-none"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <p className="text-xs text-slate-400 text-center">Draw your signature above</p>
      {hasSignature && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="clear-signature-btn"
          onClick={clearSignature}
          className="w-full text-slate-600 border-slate-300"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Clear Signature
        </Button>
      )}
    </div>
  );
};

const SWMSResult = () => {
  const navigate = useNavigate();
  const [swmsData, setSwmsData] = useState(null);
  const [editedDocument, setEditedDocument] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [savedTimestamp, setSavedTimestamp] = useState(null);
  
  // Signature state
  const [signatureData, setSignatureData] = useState(null);
  const [signedBy, setSignedBy] = useState('');
  const [signedAt, setSignedAt] = useState(null);
  const [isSigned, setIsSigned] = useState(false);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  useEffect(() => {
    // Retrieve the SWMS result from sessionStorage
    const stored = sessionStorage.getItem('swmsResult');
    if (stored) {
      const data = JSON.parse(stored);
      setSwmsData(data);
      setEditedDocument(data.documentText || '');
    } else {
      // No data, redirect back
      navigate('/tradie');
    }
  }, [navigate]);

  const handleCopy = async () => {
    if (!editedDocument) return;
    
    // Create a temporary textarea for copying
    const textArea = document.createElement('textarea');
    textArea.value = editedDocument;
    
    // Make it invisible but still functional
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    // Also set selection range for iOS
    textArea.setSelectionRange(0, editedDocument.length);
    
    let success = false;
    
    try {
      // Try execCommand first (better mobile support)
      success = document.execCommand('copy');
    } catch (err) {
      console.error('execCommand copy failed:', err);
    }
    
    // If execCommand failed, try clipboard API
    if (!success && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(editedDocument);
        success = true;
      } catch (err) {
        console.error('Clipboard API failed:', err);
      }
    }
    
    document.body.removeChild(textArea);
    
    if (success) {
      setCopied(true);
      toast.success('SWMS copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy. Try selecting the text and copying manually.');
    }
  };

  // Helper function to convert base64 to Blob
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleSave = async () => {
    // Check if signed before allowing save
    if (!isSigned) {
      toast.error('Please sign the SWMS before saving');
      return;
    }
    
    setSaving(true);
    
    try {
      // Build the full document with workers and signature info
      let fullDocument = editedDocument;
      
      // Add workers list with roles if available
      if (swmsData.workerNames) {
        const workers = swmsData.workerNames.split('\n').filter(w => w.trim());
        if (workers.length > 0) {
          fullDocument += '\n\n';
          fullDocument += 'WORKERS ON SITE & ROLE\n';
          fullDocument += '------------------------\n';
          workers.forEach(worker => {
            const trimmed = worker.trim();
            if (trimmed.includes(',')) {
              const [name, role] = trimmed.split(',').map(s => s.trim());
              fullDocument += `• ${name} (${role})\n`;
            } else {
              fullDocument += `• ${trimmed}\n`;
            }
          });
        }
      }
      
      // Add signature confirmation
      fullDocument += '\n';
      fullDocument += 'SIGNATURE CONFIRMATION\n';
      fullDocument += '------------------------\n';
      fullDocument += `Signed by: ${signedBy}\n`;
      fullDocument += `Signed at: ${formatDateTime(signedAt)}\n`;
      fullDocument += `Status: SIGNED ✓\n`;
      
      // Send to n8n webhook (URL from environment variable)
      const webhookUrl = process.env.REACT_APP_SIGNED_SWMS_WEBHOOK_URL;
      const webhookPayload = {
        title: 'Signed SWMS',
        clientName: swmsData.clientName || '',
        siteAddress: swmsData.siteAddress || '',
        tradeType: swmsData.tradeType || '',
        jobDescription: swmsData.jobDescription || '',
        datePrepared: swmsData.workDate || '',
        specialRisks: swmsData.specialRisks || '',
        workerNames: swmsData.workerNames || '',
        documentText: fullDocument,
        signatureData: signatureData,
        signedBy: signedBy,
        signedAt: signedAt,
        isSigned: isSigned
      };
      
      console.log('Sending signed SWMS to n8n:', webhookPayload);
      
      try {
        await axios.post(webhookUrl, webhookPayload, {
          headers: { 'Content-Type': 'application/json' }
        });
        console.log('Signed SWMS sent to n8n successfully');
      } catch (webhookError) {
        console.error('Webhook error:', webhookError);
        // Continue even if webhook fails - still save locally
      }
      
      // Save to local updates
      const payload = {
        job_name: 'Signed SWMS',
        site_address: swmsData.siteAddress || '',
        update_text: fullDocument,
        category: 'Deliver Work',
        urgency: '',
        required_by: ''
      };

      // Create the update
      const response = await axios.post(`${API_URL}/updates`, payload);
      const updateId = response.data.id;

      // Upload the signature image as an attachment
      if (signatureData) {
        try {
          const signatureBlob = dataURLtoBlob(signatureData);
          const formData = new FormData();
          formData.append('file', signatureBlob, 'signature.png');
          
          await axios.post(`${API_URL}/updates/${updateId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadError) {
          console.error('Signature upload error:', uploadError);
        }
      }
      
      // Show success modal instead of navigating
      setSavedTimestamp(new Date().toISOString());
      setSavedSuccessfully(true);
      setShowSuccessModal(true);
      sessionStorage.removeItem('swmsResult');
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save SWMS. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'MMM d, yyyy - h:mm a');
    } catch {
      return dateString;
    }
  };

  const handleSignatureChange = (data, hasSignature) => {
    setSignatureData(data);
    setHasDrawnSignature(hasSignature);
    // If clearing signature, also reset signed state
    if (!hasSignature) {
      setIsSigned(false);
      setSignedAt(null);
    }
  };

  const handleConfirmSignature = () => {
    if (!hasDrawnSignature) {
      toast.error('Please draw your signature first');
      return;
    }
    
    const now = new Date();
    // Default signedBy to the first worker in the list (name only, without role)
    const workers = swmsData?.workerNames?.split('\n').filter(w => w.trim()) || [];
    let firstWorker = 'Worker';
    if (workers.length > 0) {
      const firstEntry = workers[0].trim();
      // Extract just the name if it has a role (name, role format)
      firstWorker = firstEntry.includes(',') ? firstEntry.split(',')[0].trim() : firstEntry;
    }
    setSignedBy(firstWorker);
    setSignedAt(now.toISOString());
    setIsSigned(true);
    toast.success('Signature captured');
  };

  const handleClearSignature = () => {
    setSignatureData(null);
    setHasDrawnSignature(false);
    setIsSigned(false);
    setSignedAt(null);
    setSignedBy('');
  };

  if (!swmsData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-20 page-enter">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-btn"
              onClick={() => navigate('/tradie/generate-swms')}
              className="text-white hover:bg-slate-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold tracking-tight">
              Draft SWMS
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <HeaderTasksButton stage="deliver" />
            <NavMenu />
          </div>
        </div>
      </header>

      {/* Module Rail */}
      <ModuleRail stage="deliver" activeModule="swms" />

      {/* Content */}
      <main className="p-4 max-w-lg mx-auto pb-8">
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader className="pb-3">
            {/* Title - simplified single line like Draft Scope */}
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
                {swmsData.title || 'Draft SWMS'}
                {swmsData.tradeType && (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full ml-1">
                    {swmsData.tradeType}
                  </span>
                )}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                data-testid="copy-swms-btn"
                onClick={handleCopy}
                className={copied ? "bg-green-100 border-green-300" : ""}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Detail Section */}
            <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              {swmsData.clientName && (
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Client</p>
                    <p className="text-sm font-medium text-slate-700">{swmsData.clientName}</p>
                  </div>
                </div>
              )}
              {swmsData.siteAddress && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Site</p>
                    <p className="text-sm font-medium text-slate-700">{swmsData.siteAddress}</p>
                  </div>
                </div>
              )}
              {swmsData.workDate && (
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Date Prepared</p>
                    <p className="text-sm font-medium text-slate-700">{formatDate(swmsData.workDate)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Main Document Area - Editable */}
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">
                SWMS Document
              </label>
              <Textarea
                data-testid="swms-textarea"
                value={editedDocument}
                onChange={(e) => setEditedDocument(e.target.value)}
                className="min-h-[350px] text-sm text-slate-700 leading-relaxed bg-slate-50 border-slate-200 resize-y font-sans"
                placeholder="SWMS document will appear here..."
              />
            </div>

            {/* Success indicator */}
            {swmsData.success && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <Check className="w-4 h-4" />
                <span>SWMS generated successfully</span>
              </div>
            )}

            {/* Workers on Site & Role - After document, before signature */}
            {swmsData.workerNames && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-500 mb-1">Workers on Site & Role</p>
                    <div className="text-sm text-slate-700">
                      {swmsData.workerNames.split('\n').filter(w => w.trim()).map((worker, idx) => {
                        const trimmed = worker.trim();
                        const hasRole = trimmed.includes(',');
                        return (
                          <div key={idx} className="py-0.5 flex items-baseline gap-2">
                            <span className="font-medium">{hasRole ? trimmed.split(',')[0].trim() : trimmed}</span>
                            {hasRole && (
                              <span className="text-xs text-slate-500">({trimmed.split(',')[1].trim()})</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {isSigned && signedBy && (
                      <p className="text-xs text-emerald-600 mt-2 pt-2 border-t border-slate-200">
                        Signatory: <span className="font-semibold">{signedBy}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Signature Section */}
            <div className="pt-4 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <PenTool className="w-4 h-4 text-emerald-500" />
                Signature
              </h3>
              
              {!isSigned ? (
                // Signature input mode
                <div className="space-y-4">
                  <SignaturePad 
                    onSignatureChange={handleSignatureChange}
                    signatureData={signatureData}
                  />
                  
                  <Button
                    type="button"
                    data-testid="confirm-signature-btn"
                    onClick={handleConfirmSignature}
                    disabled={!hasDrawnSignature}
                    className="w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white disabled:bg-slate-300"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Confirm Signature
                  </Button>
                </div>
              ) : (
                // Signed state - show confirmed signature
                <div className="space-y-3">
                  {/* Display captured signature */}
                  {signatureData && (
                    <div className="border-2 border-emerald-200 rounded-lg p-2 bg-emerald-50">
                      <img 
                        src={signatureData} 
                        alt="Signature" 
                        className="w-full h-[100px] object-contain"
                        data-testid="signature-image"
                      />
                    </div>
                  )}
                  
                  {/* Signed details */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div>
                      <p className="text-xs text-emerald-600 font-medium">Signed by</p>
                      <p className="text-sm font-semibold text-emerald-800" data-testid="signed-by-value">
                        {signedBy}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-emerald-600 font-medium">Signed at</p>
                      <p className="text-sm font-semibold text-emerald-800" data-testid="signed-at-value">
                        {signedAt ? formatDateTime(signedAt) : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Success message */}
                  <div className="flex items-center gap-2 text-emerald-600 text-sm">
                    <Check className="w-4 h-4" />
                    <span>Signature captured</span>
                  </div>
                  
                  {/* Clear and re-sign button */}
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="clear-and-resign-btn"
                    onClick={handleClearSignature}
                    className="w-full h-10 text-slate-600 border-slate-300"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear & Re-sign
                  </Button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-2 space-y-3">
              {/* Persistent Success Confirmation - shows after save */}
              {savedSuccessfully && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-2">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div>
                      <p className="text-sm font-semibold text-emerald-700">SWMS saved and sent to 2Tap Admin</p>
                      <p className="text-xs text-emerald-600 mt-0.5">
                        Signed {savedTimestamp ? format(new Date(savedTimestamp), 'MMM d, yyyy – h:mm a') : ''}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!savedSuccessfully && (
                <>
                  <Button
                    data-testid="copy-full-btn"
                    onClick={handleCopy}
                    variant="outline"
                    className="w-full h-12 border-2"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {copied ? 'Copied!' : 'Copy SWMS Text'}
                  </Button>
                  
                  <Button
                    data-testid="save-btn"
                    onClick={handleSave}
                    disabled={saving || savedSuccessfully}
                    className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving SWMS...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Sign & Save
                      </>
                    )}
                  </Button>
                </>
              )}

              {savedSuccessfully && (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    data-testid="go-home-btn"
                    onClick={() => navigate('/tradie')}
                    className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                  >
                    Done
                  </Button>
                  <Button
                    data-testid="view-swms-btn"
                    variant="outline"
                    onClick={() => navigate('/tradie/my-updates')}
                    className="h-11 border-2 font-medium"
                  >
                    View SWMS
                  </Button>
                </div>
              )}

              {/* Workflow Prompt - Custom Branded SWMS */}
              {!savedSuccessfully && (
                <button
                  type="button"
                  data-testid="custom-branded-swms-btn"
                  onClick={() => setShowFeatureModal(true)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 hover:border-blue-400 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Palette className="w-4 h-4 text-blue-600" strokeWidth={2} />
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-semibold text-blue-600 block">Custom Branded SWMS</span>
                    <span className="text-xs text-slate-500">Add your logo, details and company format</span>
                  </div>
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Feature Modal */}
        <Dialog open={showFeatureModal} onOpenChange={setShowFeatureModal}>
          <DialogContent className="sm:max-w-md mx-4 rounded-xl border-2">
            <DialogHeader className="text-left">
              <DialogTitle className="text-lg font-bold text-slate-800">
                This feature can be added to your 2Tap setup
              </DialogTitle>
              <DialogDescription className="text-slate-600 text-sm mt-2">
                Get in touch with the 2Tap team to add this capability.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2 mt-4">
              <Button
                data-testid="contact-team-btn"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                onClick={() => {
                  setShowFeatureModal(false);
                  window.location.href = 'mailto:hello@2tap.com.au?subject=2Tap Feature Request - Custom Branded SWMS';
                }}
              >
                Contact 2Tap Team
              </Button>
              <Button
                data-testid="close-modal-btn"
                variant="outline"
                className="w-full font-semibold border-2"
                onClick={() => setShowFeatureModal(false)}
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Success Modal */}
        <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
          <DialogContent className="sm:max-w-md mx-4 rounded-xl border-2 border-emerald-200">
            <div className="text-center pt-4">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" strokeWidth={2} />
              </div>
              
              {/* Headline */}
              <h2 className="text-xl font-bold text-slate-800 mb-2">
                Signed, Saved and Sent
              </h2>
              
              {/* Body text */}
              <p className="text-slate-600 text-sm mb-6">
                Your SWMS is complete. A PDF copy has been sent to 2Tap Admin.
              </p>
              
              {/* Success confirmation lines */}
              <div className="bg-emerald-50 rounded-lg p-4 mb-6 text-left space-y-2.5">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Signature captured</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-sm font-medium text-slate-700">PDF created</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Sent to 2Tap Admin</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="space-y-2">
                <Button
                  data-testid="success-done-btn"
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/tradie');
                  }}
                >
                  Done
                </Button>
                <Button
                  data-testid="success-view-btn"
                  variant="outline"
                  className="w-full h-11 font-medium border-2"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/tradie/my-updates');
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View SWMS
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
      
      <FooterNav />
    </div>
  );
};

export default SWMSResult;
